import { Plus } from 'lucide-react';
import { useState } from 'react';

import type { AdminOrganizationRow } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { orgLabel } from '@/lib/format';

export type OrganizationChoice = { organizationId: string; name: string };

const MAX_MATCHES = 8;

/** Studio organizations matching the search, other than the ones left out. */
export function organizationMatches(organizations: AdminOrganizationRow[], exclude: Iterable<string>, query: string): OrganizationChoice[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const taken = new Set(exclude);
  return organizations
    .filter((organization) => !taken.has(organization.organizationId))
    .filter((organization) => [organization.displayName, organization.name, organization.organizationId].join(' ').toLowerCase().includes(needle))
    .slice(0, MAX_MATCHES)
    .map((organization) => ({ organizationId: organization.organizationId, name: orgLabel(organization) }));
}

/** The organization's name from the directory, or its id when it is not there. */
export function organizationName(organizations: AdminOrganizationRow[], organizationId: string): string {
  const organization = organizations.find((entry) => entry.organizationId === organizationId);
  return organization ? orgLabel(organization) : organizationId;
}

/** Search Studio organizations by name or id and add one; `exclude` hides the ones already chosen. */
export function OrganizationPicker({
  organizations,
  exclude,
  onPick,
  disabled,
  label = 'Search Studio organizations',
}: {
  organizations: AdminOrganizationRow[];
  exclude: string[];
  onPick: (organization: OrganizationChoice) => void;
  disabled?: boolean;
  label?: string;
}) {
  const [query, setQuery] = useState('');
  const matches = organizationMatches(organizations, exclude, query);

  return (
    <div className="space-y-3">
      <Input
        aria-label={label}
        placeholder="Search Studio organizations by name or id"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoComplete="off"
        disabled={disabled}
      />
      {matches.length > 0 ? (
        <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border">
          {matches.map((organization) => (
            <li key={organization.organizationId} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground">{organization.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{organization.organizationId}</span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                aria-label={`Add ${organization.name}`}
                onClick={() => {
                  onPick(organization);
                  setQuery('');
                }}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Add
              </Button>
            </li>
          ))}
        </ul>
      ) : query.trim() ? (
        <p className="text-xs text-muted-foreground">No other Studio organization matches “{query.trim()}”.</p>
      ) : null}
    </div>
  );
}
